(function () {
  // This snippet gets rendered more than once per page
  // (sections/header.liquid renders it for the desktop-inline bar, the
  // mobile bar, and the menu-drawer variant), and the surrounding
  // conditional wraps this WHOLE script block, not just the markup -
  // so without this guard, every render call defines and runs its OWN
  // independent copy of initPredictiveSearchActiveObserver,
  // each with its own `lastAppliedState` closure and its own
  // MutationObserver/click listener on `document`/`document.body`.
  // Confirmed live: with multiple copies running, one instance's own
  // class-mutation (adding `predictive-search-active` to <html>/<body>)
  // is itself a mutation every OTHER instance's observer also sees -
  // each compares it against ITS OWN `lastAppliedState`, which hadn't
  // been through the same open/close history, so they'd disagree about
  // whether the lock was already applied. The net effect on a real
  // device was exactly the reported bug: `is-active` correctly showed
  // true (`predictive-search.js` sets that directly, unaffected by this
  // duplication), but `lockBackgroundScroll()` sometimes silently never
  // ran at all, because the ONE instance whose `updateSearchActiveState`
  // actually got asked already believed (from another instance's
  // earlier, independent history) that the state hadn't changed.
  if (window.__predictiveSearchActiveObserverInitialized) return;
  window.__predictiveSearchActiveObserverInitialized = true;

  function initPredictiveSearchActiveObserver() {
    // Tracks the last applied state so the MutationObserver below (which
    // watches the very class/attribute changes this function itself
    // makes) becomes a no-op once state has already been applied —
    // without this, `activeSearch` being truthy/falsy on every observer
    // callback would re-run the classList/style writes below, which are
    // themselves mutations the observer is watching for, risking a
    // reentrant loop under Chrome's synchronous microtask-flushed
    // MutationObserver callbacks.
    let lastAppliedState = null;

    // `overflow: hidden` on <html>/<body> (still applied below via the
    // `predictive-search-active`/`search-modal-open` classes, for
    // browsers/paths that only key off that) does NOT stop iOS Safari
    // from scrolling the background page once the on-screen keyboard is
    // up - confirmed live on a current iPhone: focusing the search
    // input opens the keyboard, and the page behind the drawer still
    // pans/rubber-bands on a background swipe despite `overflow:hidden`
    // being in effect the whole time. iOS treats the visual viewport
    // (what the keyboard shrinks) independently from the layout
    // viewport `overflow` is evaluated against, so the "hidden" layout
    // viewport can still be panned within the shrunk visual one.
    // The actual fix: take <body> out of the layout viewport's own
    // scrollable flow with `position: fixed`, pinned at the negative of
    // the current scroll offset so it doesn't visually jump. A
    // position:fixed element has no scrollable box for iOS to pan
    // *inside* the visual viewport, keyboard or not - only the search
    // drawer's own (separately scrollable) content can move.
    // Owns `overflow: hidden` too (not just `position: fixed`) so every
    // caller - this file, predictive-search.js's closeSearch(), and
    // search-modal.liquid's own lock - gets identical, complete
    // behavior from one call instead of each separately remembering to
    // pair the two. Multiple call sites independently half-implementing
    // this (some only ever clearing styles, never restoring scroll) is
    // exactly how the background-scroll bug kept coming back after
    // being "fixed" - see the comment on `window.themeSearchScrollLock`
    // below.
    function lockBackgroundScroll() {
      if (document.body.dataset.searchScrollLocked === 'true') return;

      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.dataset.searchScrollLocked = 'true';
      document.body.dataset.searchScrollY = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.left = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    // Reverses `lockBackgroundScroll()` and restores the exact scroll
    // position it captured - `position: fixed` visually resets scroll
    // to 0 while locked (the page's own scroll offset is preserved only
    // via the `top` offset above), so simply clearing these styles
    // without an explicit `scrollTo` would leave the page jumped to the
    // top the moment the drawer closes.
    function unlockBackgroundScroll() {
      if (document.body.dataset.searchScrollLocked !== 'true') return;

      const scrollY = parseInt(document.body.dataset.searchScrollY || '0', 10);
      delete document.body.dataset.searchScrollLocked;
      delete document.body.dataset.searchScrollY;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    }

    // iOS reports the CURRENT keyboard-shrunk visible area through
    // `window.visualViewport`, separately from the layout viewport that
    // `100vh`/`100dvh` are computed against (the latter can stay tall
    // even with the keyboard covering half the screen). Exposing it as
    // a custom property lets the drawer's own scrollable content area
    // (predictive-search-styles.liquid) cap itself to whatever's
    // actually visible above the keyboard, instead of guessing a fixed
    // height and either leaving dead space or getting cut off/covered
    // as the keyboard opens and closes - which is what previously read
    // as "jumping" when height was left at a static viewport unit.
    function updateVisualViewportHeight() {
      if (!window.visualViewport) return;
      document.documentElement.style.setProperty('--predictive-search-vvh', window.visualViewport.height + 'px');
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateVisualViewportHeight);
      updateVisualViewportHeight();
    }

    // `assets/predictive-search.js`'s own `closeSearch()` (bound to the
    // X/reset button, Escape, and outside-click - see its own comment
    // for why those paths live there instead of here) has always done
    // its own direct `document.body.style.position/top/width/left = ''`
    // reset, independently of this file. That's harmless for the old
    // plain `overflow: hidden` lock, but is NOT harmless now: it wipes
    // the `position: fixed` this file applies WITHOUT restoring the
    // captured scroll offset first (no matching `scrollTo`), and
    // without clearing `searchScrollLocked` - so `lockBackgroundScroll`
    // sees the drawer as "already locked" on every next open and skips
    // re-applying `position: fixed` entirely. Confirmed live on a real
    // iPhone: the FIRST open locked the background correctly, but any
    // close routed through `closeSearch()` (which is most real closes -
    // the X button, Escape, tapping a result) silently broke the lock
    // for every open after that, which is why the bug kept reproducing
    // despite the fix being in place. Exposing these two functions here
    // lets `closeSearch()` call the same authoritative implementation
    // instead of maintaining its own competing copy.
    window.themeSearchScrollLock = {
      lock: lockBackgroundScroll,
      unlock: unlockBackgroundScroll,
    };

    function updateSearchActiveState() {
      const activeSearch = document.querySelector(
        '.predictive-search.is-active, dialog.search-modal__content[open], dialog[open].search-modal__content'
      );
      const isActive = !!activeSearch;
      if (isActive === lastAppliedState) return;
      lastAppliedState = isActive;

      const headerComp = document.getElementById('header-component');

      if (activeSearch) {
        document.documentElement.classList.add('predictive-search-active');
        document.body.classList.add('predictive-search-active');
        if (headerComp) {
          headerComp.dataset.stickyState = 'active';
        }
        // Deferred one frame on purpose: `position: sticky` computes
        // against the CURRENT scroll offset of the nearest scrollable
        // ancestor. Setting `overflow: hidden` on <html>/<body> in the
        // same synchronous tick as `stickyState = 'active'` above froze
        // the scroll container before the browser ever got a chance to
        // paint the header pinned at its stuck position — so it fell
        // back to rendering at its natural (far off-screen, scrolled-
        // past) position instead. Confirmed live: scrolling down and
        // opening the dropdown left the header entirely off-screen
        // (or, per a client report, only partially visible mid-
        // transition) even though `stickyState` was correctly 'active'.
        // Applying the lock one frame later lets that first paint
        // happen while the page is still genuinely scrollable, so the
        // sticky position is correctly established before scrolling is
        // frozen.
        requestAnimationFrame(function () {
          // Deferred one frame for the same reason `overflow: hidden`
          // used to be deferred alone (see above): `lockBackgroundScroll`
          // now applies BOTH `overflow: hidden` and `position: fixed` in
          // one call, and both pull <body> out of the document's normal
          // scrollable flow, so applying either in the same tick as
          // `stickyState = 'active'` would freeze the header's sticky
          // positioning before its first "active" paint.
          lockBackgroundScroll();
        });
      } else {
        document.documentElement.classList.remove('predictive-search-active', 'search-modal-open');
        document.body.classList.remove('predictive-search-active', 'search-modal-open');
        document.documentElement.removeAttribute('data-search-modal-open');
        document.body.removeAttribute('data-search-modal-open');
        unlockBackgroundScroll();
        if (headerComp && (window.scrollY || 0) === 0) {
          headerComp.dataset.stickyState = 'inactive';
        }
      }
    }

    // `updateSearchActiveState()` is the ONLY code that ever releases
    // the `overflow: hidden` scroll-lock it applies to <html>/<body>
    // when a dropdown opens — but it was only ever re-run from this
    // file's own click handler below, scoped narrowly to the explicit
    // close-button/overlay click path. `assets/predictive-search.js`
    // closes the dropdown through several OTHER paths it owns
    // independently — outside-click (`#handleOutsideClick`), the
    // Escape key (`#handleKeyboardShortcut`), and its own X/reset
    // button — none of which call back into this file. Confirmed live:
    // closing the dropdown via any of those paths removed `.is-active`
    // correctly, but left `<html>`/`<body>` permanently
    // `overflow: hidden`-locked (scroll never worked again on that
    // page load), which is what read to users as "the page scrolled to
    // the top and search doesn't open properly" — the page hadn't
    // actually scrolled, it was just stuck unable to. A MutationObserver
    // on the `class`/`open` attributes that actually gate `activeSearch`
    // makes the lock reactive to ANY of these close paths, not just the
    // ones this file's own listener happens to know about.
    new MutationObserver(updateSearchActiveState).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'open'],
      subtree: true
    });

    document.addEventListener(
      'click',
      function (e) {
        const isCloseButton = e.target && e.target.closest(
          '.predictive-search__close-modal-button, .predictive-search__panel-reset, .predictive-search__reset-button, [ref="panelPillReset"], [ref="closeModalButton"], [ref="resetButton"]'
        );
        const isOverlay = e.target && e.target.classList.contains('predictive-search-overlay');

        if (isCloseButton || isOverlay) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          document.querySelectorAll('.predictive-search.is-active, predictive-search-component.is-active').forEach(function (el) {
            el.classList.remove('is-active');
          });
          const modal = document.getElementById('search-modal');
          if (modal && typeof modal.closeDialog === 'function') {
            modal.closeDialog();
          }
          document.querySelectorAll('input[type="search"], .search-input, .search-action__header-field-input').forEach(function (input) {
            input.blur();
          });

          document.documentElement.classList.remove('predictive-search-active', 'search-modal-open');
          document.body.classList.remove('predictive-search-active', 'search-modal-open');
          document.documentElement.removeAttribute('data-search-modal-open');
          document.body.removeAttribute('data-search-modal-open');
          unlockBackgroundScroll();

          updateSearchActiveState();
          return;
        }

        const isButton = e.target.closest('button, [role="button"]');
        if (isButton) return;

        const headerPill = e.target.closest('.search-action__header-field');
        if (headerPill) {
          const predictiveComp = headerPill.closest('predictive-search-component');
          if (predictiveComp) {
            predictiveComp.classList.add('is-active');
            const panelInput = predictiveComp.querySelector('.search-action__header-field-input--panel, input[type="search"]');
            if (panelInput) {
              setTimeout(function () {
                // `preventScroll` matters here specifically: `stickyState`
                // is set to 'active' a few lines below via
                // `updateSearchActiveState()`, but that doesn't
                // guarantee the header has actually finished
                // transitioning into its sticky/pinned position by the
                // time this 30ms timeout fires. If the header is still
                // sitting at its natural (unstickied, scrolled-past)
                // position in the document, a plain `.focus()` call
                // triggers the browser's own "scroll the focused
                // element into view" behavior — jumping the whole page
                // to the header's natural position. That's what read as
                // "the page scrolls to the top" with the header
                // half-visible mid-transition. Confirmed live.
                panelInput.focus({ preventScroll: true });
              }, 30);
            }
            updateSearchActiveState();
          }
        }
      },
      true
    );

    updateSearchActiveState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPredictiveSearchActiveObserver);
  } else {
    initPredictiveSearchActiveObserver();
  }
})();
